import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import { Badge } from './badge'

/**
 * Tabs — compound tab component (TabRoot / TabList / TabTrigger / TabContent),
 * adopted from the TailGrids tabs model and the Figma tab specs.
 *
 *   variant    default | minimal
 *   direction  horizontal | vertical   (orientation of the tab list)
 *   size       md | sm                  (md = the existing Figma/TailGrids size)
 *
 * - default (segmented): horizontal renders a gray-100 container with a white,
 *   shadowed active "card"; vertical renders stacked items with a filled basil-50
 *   active pill (matching Figma's vertical tabs).
 * - minimal (underline): a basil-700 border on the active edge (bottom for
 *   horizontal, left for vertical) over a neutral baseline.
 *
 * TabTrigger supports an `icon` (before the label) and a `badge` (rendered with
 * the primary <Badge>, per the design). Fully keyboard accessible: arrow keys,
 * Home/End move between tabs with roving tabindex and proper ARIA wiring.
 *
 * Controlled (`value` + `onValueChange`) or uncontrolled (`defaultValue`).
 *
 * Note: `direction` defaults to `horizontal` (top tab bar) — the common case —
 * which differs from the TailGrids doc's `vertical` default.
 */

type TabsDirection = 'horizontal' | 'vertical'
type TabsSize = 'md' | 'sm'
type TabsVariant = 'default' | 'minimal'

interface TabsContextValue {
  value: string
  setValue: (value: string) => void
  variant: TabsVariant
  size: TabsSize
  direction: TabsDirection
  baseId: string
}

const TabsContext = React.createContext<TabsContextValue | null>(null)

function useTabsContext(component: string): TabsContextValue {
  const ctx = React.useContext(TabsContext)
  if (!ctx) {
    throw new Error(`<${component}> must be used within <TabRoot>`)
  }
  return ctx
}

const triggerId = (baseId: string, value: string) =>
  `${baseId}-trigger-${value}`
const panelId = (baseId: string, value: string) => `${baseId}-panel-${value}`

export interface TabRootProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  /** Initially active tab (uncontrolled). */
  defaultValue?: string
  /** Active tab (controlled). Pair with `onValueChange`. */
  value?: string
  /** Called with the new tab value when the active tab changes. */
  onValueChange?: (value: string) => void
  variant?: TabsVariant
  direction?: TabsDirection
  size?: TabsSize
}

export const TabRoot = React.forwardRef<HTMLDivElement, TabRootProps>(
  (
    {
      className,
      defaultValue,
      value: valueProp,
      onValueChange,
      variant = 'default',
      direction = 'horizontal',
      size = 'md',
      children,
      ...props
    },
    ref
  ) => {
    const baseId = React.useId()
    const [uncontrolled, setUncontrolled] = React.useState(defaultValue ?? '')
    const isControlled = valueProp !== undefined
    const value = isControlled ? valueProp : uncontrolled

    const setValue = React.useCallback(
      (next: string) => {
        if (!isControlled) setUncontrolled(next)
        onValueChange?.(next)
      },
      [isControlled, onValueChange]
    )

    const ctx = React.useMemo<TabsContextValue>(
      () => ({ value, setValue, variant, size, direction, baseId }),
      [value, setValue, variant, size, direction, baseId]
    )

    return (
      <TabsContext.Provider value={ctx}>
        <div
          ref={ref}
          className={cn(
            'flex',
            direction === 'vertical' ? 'flex-row gap-6' : 'flex-col gap-[1rem]',
            className
          )}
          {...props}
        >
          {children}
        </div>
      </TabsContext.Provider>
    )
  }
)
TabRoot.displayName = 'TabRoot'

const tabListVariants = cva('flex', {
  variants: {
    variant: { default: '', minimal: '' },
    direction: { horizontal: '', vertical: 'flex-col' },
  },
  compoundVariants: [
    {
      variant: 'default',
      direction: 'horizontal',
      className: 'items-center gap-1 rounded-lg bg-fbui-gray-100 p-1',
    },
    { variant: 'default', direction: 'vertical', className: 'gap-1' },
    {
      variant: 'minimal',
      direction: 'horizontal',
      className: 'items-center gap-1 border-b border-fbui-gray-200',
    },
    {
      variant: 'minimal',
      direction: 'vertical',
      className: 'gap-1 border-l border-fbui-gray-200',
    },
  ],
  defaultVariants: { variant: 'default', direction: 'horizontal' },
})

export type TabListProps = React.HTMLAttributes<HTMLDivElement>

export const TabList = React.forwardRef<HTMLDivElement, TabListProps>(
  ({ className, onKeyDown, ...props }, ref) => {
    const { variant, direction } = useTabsContext('TabList')

    // Roving keyboard nav across the enabled tabs in this list.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(e)
      const nextKey = direction === 'vertical' ? 'ArrowDown' : 'ArrowRight'
      const prevKey = direction === 'vertical' ? 'ArrowUp' : 'ArrowLeft'
      if (!['Home', 'End', nextKey, prevKey].includes(e.key)) return

      const tabs = Array.from(
        e.currentTarget.querySelectorAll<HTMLButtonElement>(
          '[role="tab"]:not([disabled])'
        )
      )
      if (tabs.length === 0) return
      const current = tabs.indexOf(document.activeElement as HTMLButtonElement)
      let next = current
      if (e.key === 'Home') next = 0
      else if (e.key === 'End') next = tabs.length - 1
      // When focus isn't on a tab yet (current === -1), enter from the edge.
      else if (current === -1) next = e.key === nextKey ? 0 : tabs.length - 1
      else if (e.key === nextKey) next = (current + 1) % tabs.length
      else if (e.key === prevKey)
        next = (current - 1 + tabs.length) % tabs.length

      e.preventDefault()
      tabs[next]?.focus()
      tabs[next]?.click()
    }

    return (
      <div
        ref={ref}
        role="tablist"
        aria-orientation={direction}
        className={cn(tabListVariants({ variant, direction }), className)}
        onKeyDown={handleKeyDown}
        {...props}
      />
    )
  }
)
TabList.displayName = 'TabList'

const tabTriggerVariants = cva(
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium tracking-[-0.2px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-fbui-ring-default disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: { default: '', minimal: '' },
      direction: { horizontal: '', vertical: 'w-full justify-start text-left' },
      size: {
        md: 'text-sm [&_svg]:size-5',
        sm: 'text-xs [&_svg]:size-4',
      },
    },
    compoundVariants: [
      // default (segmented) — white shadowed active card
      {
        variant: 'default',
        className:
          'rounded-md text-fbui-gray-500 hover:text-fbui-gray-700 data-[active=true]:bg-fbui-base-white data-[active=true]:text-fbui-gray-800 data-[active=true]:shadow-fbui-xs',
      },
      // default vertical — filled basil active pill
      {
        variant: 'default',
        direction: 'vertical',
        className:
          'rounded-lg data-[active=true]:bg-fbui-basil-50 data-[active=true]:text-fbui-basil-700 data-[active=true]:shadow-none',
      },
      // minimal (underline) — basil bottom border
      {
        variant: 'minimal',
        direction: 'horizontal',
        className:
          '-mb-px rounded-none border-b-2 border-transparent text-fbui-gray-500 hover:text-fbui-gray-700 data-[active=true]:border-fbui-basil-700 data-[active=true]:text-fbui-basil-700',
      },
      // minimal vertical — basil left border
      {
        variant: 'minimal',
        direction: 'vertical',
        className:
          '-ml-px rounded-none border-l-2 border-transparent text-fbui-gray-500 hover:text-fbui-gray-700 data-[active=true]:border-fbui-basil-700 data-[active=true]:text-fbui-basil-700',
      },
      // segmented sizing (fixed height)
      { variant: 'default', size: 'md', className: 'h-9 px-[0.75rem]' },
      { variant: 'default', size: 'sm', className: 'h-7 px-2.5' },
      // underline sizing (vertical padding)
      { variant: 'minimal', size: 'md', className: 'px-[0.75rem] py-[0.75rem]' },
      { variant: 'minimal', size: 'sm', className: 'px-2.5 py-2' },
    ],
    defaultVariants: {
      variant: 'default',
      direction: 'horizontal',
      size: 'md',
    },
  }
)

export interface TabTriggerProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<
      VariantProps<typeof tabTriggerVariants>,
      'variant' | 'direction' | 'size'
    > {
  /** Unique tab identifier, matched to a TabContent `value`. */
  value: string
  /** Optional icon rendered before the label (auto-sized per `size`). */
  icon?: React.ReactNode
  /** Optional badge rendered after the label (primary <Badge>). */
  badge?: string | number
}

export const TabTrigger = React.forwardRef<HTMLButtonElement, TabTriggerProps>(
  ({ className, value, icon, badge, children, onClick, ...props }, ref) => {
    const ctx = useTabsContext('TabTrigger')
    const active = ctx.value === value
    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        id={triggerId(ctx.baseId, value)}
        aria-selected={active}
        aria-controls={panelId(ctx.baseId, value)}
        data-active={active}
        tabIndex={active ? 0 : -1}
        className={cn(
          tabTriggerVariants({
            variant: ctx.variant,
            direction: ctx.direction,
            size: ctx.size,
          }),
          className
        )}
        onClick={(e) => {
          onClick?.(e)
          ctx.setValue(value)
        }}
        {...props}
      >
        {icon}
        {children}
        {badge !== undefined && (
          <Badge color="primary" size="sm">
            {badge}
          </Badge>
        )}
      </button>
    )
  }
)
TabTrigger.displayName = 'TabTrigger'

export interface TabContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Tab identifier matching the corresponding TabTrigger `value`. */
  value: string
}

export const TabContent = React.forwardRef<HTMLDivElement, TabContentProps>(
  ({ className, value, ...props }, ref) => {
    const ctx = useTabsContext('TabContent')
    const active = ctx.value === value
    return (
      <div
        ref={ref}
        role="tabpanel"
        id={panelId(ctx.baseId, value)}
        aria-labelledby={triggerId(ctx.baseId, value)}
        hidden={!active}
        tabIndex={0}
        className={cn('outline-none', className)}
        {...props}
      />
    )
  }
)
TabContent.displayName = 'TabContent'
